import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { useCompanyFilter } from "@/lib/companyFilter";
import { isAttendanceEligibleOnDate } from "@/lib/attendanceFlags";
import { formatDisplayDate } from "@/lib/dateFormat";
import { SortableTH, useSortableData } from "@/components/SortableTable";
import {
  Plus,
  ClipboardCheck,
  Upload,
  FileSpreadsheet,
  FileText,
  RefreshCw,
  CalendarRange,
  Pencil,
  Trash2,
  X,
  Zap,
  Home,
  CalendarHeart,
  CalendarOff,
} from "lucide-react";

const MONTHS = [
  "Jan","Feb","Mar","Apr","May","Jun",
  "Jul","Aug","Sep","Oct","Nov","Dec",
];

const STATUS_COLORS = {
  Present: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Absent: "bg-rose-100 text-rose-700 border-rose-200",
  "Half-day": "bg-amber-100 text-amber-700 border-amber-200",
  WFH: "bg-blue-100 text-blue-700 border-blue-200",
  Leave: "bg-purple-100 text-purple-700 border-purple-200",
  Holiday: "bg-slate-100 text-slate-600 border-slate-200",
  "Weekly Off": "bg-indigo-100 text-indigo-700 border-indigo-200",
};

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

function attendanceEmployeeSortKey(emp) {
  const join = ((emp?.employment?.joining_date) || "").toString().slice(0, 10) || "9999-99-99";
  const code = ((emp?.basic?.code) || "").toString().toLowerCase();
  return [join, code];
}

function compareAttendanceEmployees(empA, empB) {
  const [ja, ca] = attendanceEmployeeSortKey(empA);
  const [jb, cb] = attendanceEmployeeSortKey(empB);
  if (ja !== jb) return ja < jb ? -1 : 1;
  if (ca !== cb) return ca < cb ? -1 : 1;
  return 0;
}

export default function Attendance() {
  const { companyId, companyOptions, setCompanyId, empParams } = useCompanyFilter();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [tab, setTab] = useState("list"); // list | summary
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", date: "", status: "Present", check_in: "", check_out: "", note: "" });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("EMP001,2026-01-15,Present\nEMP002,2026-01-15,Absent");
  const [bulkResult, setBulkResult] = useState(null);
  const [editing, setEditing] = useState(null); // attendance record being edited
  const [editForm, setEditForm] = useState({ status: "Present", check_in: "", check_out: "", note: "" });
  const [deleting, setDeleting] = useState(null);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickDate, setQuickDate] = useState(new Date().toISOString().slice(0, 10));
  const [quickBusy, setQuickBusy] = useState(false);
  const [quickResult, setQuickResult] = useState(null);
  const [todaySummary, setTodaySummary] = useState(null);
  // Phase 18 — single-date filter (default = Today). When non-empty, table filters to this date only.
  const [filterDate, setFilterDate] = useState(new Date().toISOString().slice(0, 10));
  // Monthly Summary — per-column filters (text / status / min count)
  const emptySumFilters = {
    code: "",
    name: "",
    status: "",
    present: "",
    absent: "",
    half_day: "",
    wfh: "",
    leave: "",
    holiday: "",
    weekly_off: "",
  };
  const [sumFilters, setSumFilters] = useState(emptySumFilters);

  const load = async () => {
    // Phase 18 — When a single filterDate is set, derive month/year from it so
    // we fetch the right month's records (then filter to the exact date client-side).
    let m = month, y = year;
    if (filterDate && tab === "list") {
      const d = new Date(filterDate + "T00:00:00");
      m = d.getMonth() + 1; y = d.getFullYear();
    }
    const [emps, recs, sum, today] = await Promise.all([
      api.get("/employees", { params: empParams }),
      api.get("/attendance", { params: { month: m, year: y, ...empParams } }),
      api.get("/attendance/summary", { params: { month: m, year: y } }),
      api.get("/attendance/today-summary").catch(() => ({ data: null })),
    ]);
    setEmployees(emps.data);
    setRecords(recs.data);
    setSummary(sum.data);
    setTodaySummary(today.data || null);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [month, year, filterDate, tab, companyId]);

  // Drive view from URL (?tab=summary | list). Actions open modals then clear ?action=
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const view = searchParams.get("tab");
    setTab(view === "summary" ? "summary" : "list");
    const action = searchParams.get("action");
    if (!action) return;
    if (action === "add") setAddOpen(true);
    else if (action === "bulk-upload") setBulkOpen(true);
    else if (action === "quick-mark") {
      setQuickDate(new Date().toISOString().slice(0, 10));
      setQuickResult(null);
      setQuickOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("action");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const empMap = useMemo(
    () => Object.fromEntries(employees.map((e) => [e.id, e])),
    [employees]
  );

  // Working roster for Add Attendance — status + include flag + joining/LWD vs selected date
  const ATTENDANCE_ROSTER = useMemo(
    () => ["Active", "Onboarding", "Probation", "Notice Period"],
    []
  );
  const rosterEmployees = useMemo(() => {
    const day = form.date || new Date().toISOString().slice(0, 10);
    return employees
      .filter((e) => isAttendanceEligibleOnDate(e, day, ATTENDANCE_ROSTER).ok)
      .sort(compareAttendanceEmployees);
  }, [employees, ATTENDANCE_ROSTER, form.date]);

  const summaryRows = summary?.summary || [];
  const summaryStatusOptions = useMemo(() => {
    const set = new Set(summaryRows.map((r) => r.status).filter(Boolean));
    return [...set].sort();
  }, [summaryRows]);

  const filteredSummary = useMemo(() => {
    const qCode = (sumFilters.code || "").trim().toLowerCase();
    const qName = (sumFilters.name || "").trim().toLowerCase();
    const qStatus = sumFilters.status || "";
    const minOf = (key) => {
      const raw = (sumFilters[key] || "").trim();
      if (raw === "") return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    };
    const mins = {
      present: minOf("present"),
      absent: minOf("absent"),
      half_day: minOf("half_day"),
      wfh: minOf("wfh"),
      leave: minOf("leave"),
      holiday: minOf("holiday"),
      weekly_off: minOf("weekly_off"),
    };
    const rows = summaryRows
      .filter((s) => {
        if (qCode && !(s.employee_code || "").toLowerCase().includes(qCode)) return false;
        if (qName && !(s.employee_name || "").toLowerCase().includes(qName)) return false;
        if (qStatus && s.status !== qStatus) return false;
        if (mins.present != null && (s.present || 0) < mins.present) return false;
        if (mins.absent != null && (s.absent || 0) < mins.absent) return false;
        if (mins.half_day != null && (s.half_day || 0) < mins.half_day) return false;
        if (mins.wfh != null && (s.wfh || 0) < mins.wfh) return false;
        if (mins.leave != null && (s.leave || 0) < mins.leave) return false;
        if (mins.holiday != null && (s.holiday || 0) < mins.holiday) return false;
        if (mins.weekly_off != null && (s.weekly_off || 0) < mins.weekly_off) return false;
        return true;
      })
      .map((s) => {
        const emp = empMap[s.employee_id];
        return {
          ...s,
          joining_date: ((emp?.employment?.joining_date) || "").toString().slice(0, 10) || "9999-99-99",
        };
      })
      .sort((a, b) => {
        const empA = empMap[a.employee_id];
        const empB = empMap[b.employee_id];
        if (empA || empB) return compareAttendanceEmployees(empA, empB);
        const ca = (a.employee_code || "").toString().toLowerCase();
        const cb = (b.employee_code || "").toString().toLowerCase();
        if (ca !== cb) return ca < cb ? -1 : 1;
        return 0;
      });
    return rows;
  }, [summaryRows, sumFilters, empMap]);

  const {
    items: sortedSummary,
    sortBy: sumSortBy,
    sortDir: sumSortDir,
    requestSort: requestSumSort,
  } = useSortableData(filteredSummary, { initialKey: "joining_date", initialDir: "asc" });

  const sumFiltersActive = useMemo(
    () => Object.values(sumFilters).some((v) => String(v || "").trim() !== ""),
    [sumFilters]
  );

  const setSumFilter = (key, value) => setSumFilters((prev) => ({ ...prev, [key]: value }));

  const submitAttendance = async (e) => {
    e.preventDefault();
    await api.post("/attendance", form);
    setAddOpen(false);
    setForm({ employee_id: "", date: "", status: "Present", check_in: "", check_out: "", note: "" });
    load();
  };

  const openEdit = (rec) => {
    setEditing(rec);
    setEditForm({
      status: rec.status || "Present",
      check_in: rec.check_in || "",
      check_out: rec.check_out || "",
      note: rec.note || "",
    });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    await api.put(`/attendance/${editing.id}`, {
      status: editForm.status,
      check_in: editForm.check_in || "",
      check_out: editForm.check_out || "",
      note: editForm.note || "",
    });
    setEditing(null);
    load();
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/attendance/${deleting.id}`);
      setDeleting(null);
      load();
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Failed to delete");
    }
  };

  const runQuickMark = async (status) => {
    if (!quickDate) return;
    setQuickBusy(true);
    setQuickResult(null);
    try {
      const res = await api.post("/attendance/mark-all", null, {
        params: { date_str: quickDate, status },
      });
      setQuickResult({ ok: true, ...res.data });
      await load();
    } catch (ex) {
      setQuickResult({ ok: false, error: ex?.response?.data?.detail || "Failed" });
    } finally {
      setQuickBusy(false);
    }
  };

  const openQuick = () => {
    setQuickDate(new Date().toISOString().slice(0, 10));
    setQuickResult(null);
    setQuickOpen(true);
  };

  const submitBulk = async () => {
    const rows = bulkText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => {
        const [employee_code, date, status, check_in, check_out, note] = l.split(",").map((p) => (p || "").trim());
        return { employee_code, date, status, check_in: check_in || "", check_out: check_out || "", note: note || "" };
      });
    try {
      const r = await api.post("/attendance/bulk", rows);
      setBulkResult(r.data);
      load();
    } catch (e) {
      setBulkResult({ created: 0, skipped: rows.length, errors: [e?.response?.data?.detail || "Failed"] });
    }
  };

  const downloadFile = async (path, name) => {
    const res = await api.get(path, { responseType: "blob" });
    const blob = new Blob([res.data]);
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const exportAttendance = (fmt) => {
    if (tab === "list") {
      const day = filterDate || new Date().toISOString().slice(0, 10);
      const name = `attendance_${day}.${fmt}`;
      return downloadFile(`/exports/attendance.${fmt}?date=${encodeURIComponent(day)}`, name);
    }
    const name = `attendance_${year}_${String(month).padStart(2, "0")}.${fmt}`;
    return downloadFile(`/exports/attendance.${fmt}?month=${month}&year=${year}`, name);
  };

  const visibleRecords = useMemo(() => {
    const rows = filterDate ? records.filter((r) => r.date === filterDate) : [...records];
    const enriched = rows.map((r) => {
      const emp = empMap[r.employee_id];
      return {
        ...r,
        employee_code: emp?.basic?.code || "",
        employee_name: emp
          ? [emp.basic.first_name, emp.basic.middle_name, emp.basic.last_name].filter(Boolean).join(" ")
          : "",
        joining_date: ((emp?.employment?.joining_date) || "").toString().slice(0, 10) || "9999-99-99",
      };
    });
    // Base order (used when sort is cleared): joining date ASC, then employee code
    return enriched.sort((a, b) => {
      if (!filterDate) {
        const da = a.date || "";
        const db = b.date || "";
        if (da !== db) return da < db ? -1 : 1;
      }
      return compareAttendanceEmployees(empMap[a.employee_id], empMap[b.employee_id]);
    });
  }, [records, filterDate, empMap]);

  const {
    items: sortedRecords,
    sortBy: listSortBy,
    sortDir: listSortDir,
    requestSort: requestListSort,
  } = useSortableData(visibleRecords, { initialKey: "joining_date", initialDir: "asc" });

  const todayCards = [
    { key: "Present", label: "Present", color: "#089949" },
    { key: "Absent", label: "Absent", color: "#dd4b39" },
    { key: "WFH", label: "WFH", color: "#226DB4" },
    { key: "Leave", label: "Leave", color: "#605ca8" },
    { key: "Not Marked", label: "Not Marked", color: "#f39c12" },
    { key: "Total", label: "Roster", color: "#64748b" },
  ];

  return (
    <AdminLayout
      title={tab === "summary" ? "Monthly Attendance Summary" : "Daily Attendance"}
      breadcrumbs={["Home", "HR", "Attendance", tab === "summary" ? "Monthly Summary" : "Daily"]}
    >
      {/* Today overview — only on Daily view */}
      {tab === "list" && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4" data-testid="att-today-strip">
          {todayCards.map((c) => (
            <div key={c.key} className="bg-white border border-slate-200 rounded px-3 py-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">{c.label}</div>
              <div className="text-2xl font-semibold mt-1" style={{ color: c.color }}>
                {todaySummary ? (todaySummary[c.key] ?? 0) : "—"}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Page actions — Mark / Quick Mark only here (not in sidebar) */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {tab === "list" && (
          <>
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded"
              data-testid="att-add-btn"
            >
              <Plus className="h-4 w-4" /> Mark Attendance
            </button>
            <button
              type="button"
              onClick={openQuick}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded"
              data-testid="att-quick-mark-btn"
            >
              <Zap className="h-4 w-4" /> Quick Mark All
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => exportAttendance("xlsx")}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50 text-slate-700"
          title={tab === "list" ? "Export selected day" : "Export selected month"}
          data-testid="export-att-xlsx-btn"
        >
          <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Excel
        </button>
        <button
          type="button"
          onClick={() => exportAttendance("pdf")}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50 text-slate-700"
          title={tab === "list" ? "Export selected day" : "Export selected month"}
          data-testid="export-att-pdf-btn"
        >
          <FileText className="h-4 w-4 text-rose-600" /> PDF
        </button>
        <button
          type="button"
          onClick={load}
          className="ml-auto p-2 border border-slate-200 rounded hover:bg-slate-50 text-slate-600"
          title="Refresh"
          data-testid="att-refresh-btn"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded shadow-sm">
        <div className="px-5 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 py-3">
          <div className="text-sm font-semibold text-slate-800">
            {tab === "list" ? "Daily records" : "Monthly roster summary"}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              className="px-3 py-1.5 text-sm border border-slate-200 rounded bg-white min-w-[160px]"
              data-testid="att-company-select"
            >
              {companyOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {tab === "list" ? (
              <div className="flex items-center gap-2 flex-wrap" data-testid="att-filter-date-group">
                <span className="text-xs text-slate-500">Date</span>
                <input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-200 rounded"
                  data-testid="att-filter-date-picker"
                />
                <button
                  type="button"
                  onClick={() => setFilterDate(new Date().toISOString().slice(0, 10))}
                  className="px-2 py-1.5 text-xs border border-slate-200 rounded hover:bg-slate-50"
                  data-testid="att-filter-today"
                >
                  Today
                </button>
                {filterDate && (
                  <button
                    type="button"
                    onClick={() => setFilterDate("")}
                    className="text-xs px-2 py-1.5 text-slate-500 hover:text-slate-700"
                    title="Show whole month"
                    data-testid="att-filter-date-clear"
                  >
                    Clear
                  </button>
                )}
              </div>
            ) : (
              <>
                <select
                  value={month}
                  onChange={(e) => setMonth(parseInt(e.target.value, 10))}
                  className="px-3 py-1.5 text-sm border border-slate-200 rounded bg-white"
                  data-testid="att-month-select"
                >
                  {MONTHS.map((m, i) => (
                    <option key={m} value={i + 1}>{m}</option>
                  ))}
                </select>
                <select
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value, 10))}
                  className="px-3 py-1.5 text-sm border border-slate-200 rounded bg-white"
                  data-testid="att-year-select"
                >
                  {[2024, 2025, 2026, 2027].map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </>
            )}
          </div>
        </div>

      {tab === "list" ? (
        <div className="overflow-x-auto">
          <div className="px-5 py-2.5 text-xs text-slate-500 border-b border-slate-100">
            Showing <b>{sortedRecords.length}</b> record{sortedRecords.length === 1 ? "" : "s"}
            {filterDate ? <> for <b>{formatDisplayDate(filterDate)}</b></> : <> in <b>{MONTHS[month - 1]} {year}</b></>}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <SortableTH columnKey="date" active={listSortBy} dir={listSortDir} onClick={requestListSort}>Date</SortableTH>
                <SortableTH columnKey="employee_code" active={listSortBy} dir={listSortDir} onClick={requestListSort}>Code</SortableTH>
                <SortableTH columnKey="employee_name" active={listSortBy} dir={listSortDir} onClick={requestListSort}>Employee</SortableTH>
                <SortableTH columnKey="status" active={listSortBy} dir={listSortDir} onClick={requestListSort}>Status</SortableTH>
                <SortableTH columnKey="check_in" active={listSortBy} dir={listSortDir} onClick={requestListSort}>Check-in</SortableTH>
                <SortableTH columnKey="check_out" active={listSortBy} dir={listSortDir} onClick={requestListSort}>Check-out</SortableTH>
                <SortableTH columnKey="note" active={listSortBy} dir={listSortDir} onClick={requestListSort}>Note</SortableTH>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedRecords.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-slate-400" data-testid="att-list-empty">
                    No attendance records for {filterDate ? formatDisplayDate(filterDate) : `${MONTHS[month - 1]} ${year}`}
                  </td>
                </tr>
              ) : (
                sortedRecords.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-3 text-slate-700 font-medium">{formatDisplayDate(r.date)}</td>
                    <td className="px-4 py-3 text-slate-600">{r.employee_code || "—"}</td>
                    <td className="px-4 py-3 text-slate-800">{r.employee_name || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] px-2 py-1 rounded border font-semibold ${STATUS_COLORS[r.status] || ""}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{r.check_in || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.check_out || "—"}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{r.note || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => openEdit(r)}
                          data-testid={`edit-att-${r.id}`}
                          title="Edit"
                          className="p-1.5 text-[#226DB4] hover:bg-[#226DB4]/10 rounded"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeleting(r)}
                          data-testid={`delete-att-${r.id}`}
                          title="Delete"
                          className="p-1.5 text-rose-500 hover:bg-rose-50 rounded"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="px-5 py-3 border-b border-slate-200 text-sm text-slate-600 flex items-center gap-2 flex-wrap">
            <CalendarRange className="h-4 w-4 text-[#226DB4]" />
            <span>
              <b>{MONTHS[month - 1]} {year}</b> · {summary?.total_days || 0} calendar days
              {" · "}
              {sortedSummary.length}
              {sumFiltersActive ? ` of ${summaryRows.length}` : ""} employees
            </span>
            {sumFiltersActive && (
              <button
                type="button"
                onClick={() => setSumFilters(emptySumFilters)}
                className="ml-auto text-xs text-slate-500 hover:text-slate-700 underline"
                data-testid="summary-filter-reset"
              >
                Clear column filters
              </button>
            )}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <SortableTH columnKey="employee_code" active={sumSortBy} dir={sumSortDir} onClick={requestSumSort}>Code</SortableTH>
                <SortableTH columnKey="employee_name" active={sumSortBy} dir={sumSortDir} onClick={requestSumSort}>Name</SortableTH>
                <SortableTH columnKey="status" active={sumSortBy} dir={sumSortDir} onClick={requestSumSort}>Status</SortableTH>
                <SortableTH columnKey="present" active={sumSortBy} dir={sumSortDir} onClick={requestSumSort} align="right">Present</SortableTH>
                <SortableTH columnKey="absent" active={sumSortBy} dir={sumSortDir} onClick={requestSumSort} align="right">Absent</SortableTH>
                <SortableTH columnKey="half_day" active={sumSortBy} dir={sumSortDir} onClick={requestSumSort} align="right">Half-day</SortableTH>
                <SortableTH columnKey="wfh" active={sumSortBy} dir={sumSortDir} onClick={requestSumSort} align="right">WFH</SortableTH>
                <SortableTH columnKey="leave" active={sumSortBy} dir={sumSortDir} onClick={requestSumSort} align="right">Leave</SortableTH>
                <SortableTH columnKey="holiday" active={sumSortBy} dir={sumSortDir} onClick={requestSumSort} align="right">Holiday</SortableTH>
                <SortableTH columnKey="weekly_off" active={sumSortBy} dir={sumSortDir} onClick={requestSumSort} align="right">Weekly Off</SortableTH>
              </tr>
              <tr className="bg-white border-t border-slate-100 normal-case tracking-normal font-normal">
                <th className="px-2 py-2">
                  <input
                    type="text"
                    value={sumFilters.code}
                    onChange={(e) => setSumFilter("code", e.target.value)}
                    placeholder="Filter…"
                    className="w-full border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#226DB4]/40"
                    data-testid="summary-filter-code"
                  />
                </th>
                <th className="px-2 py-2">
                  <input
                    type="text"
                    value={sumFilters.name}
                    onChange={(e) => setSumFilter("name", e.target.value)}
                    placeholder="Filter…"
                    className="w-full border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#226DB4]/40"
                    data-testid="summary-filter-name"
                  />
                </th>
                <th className="px-2 py-2">
                  <select
                    value={sumFilters.status}
                    onChange={(e) => setSumFilter("status", e.target.value)}
                    className="w-full border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#226DB4]/40 bg-white"
                    data-testid="summary-filter-status"
                  >
                    <option value="">All</option>
                    {summaryStatusOptions.map((st) => (
                      <option key={st} value={st}>{st}</option>
                    ))}
                  </select>
                </th>
                {[
                  ["present", "summary-filter-present"],
                  ["absent", "summary-filter-absent"],
                  ["half_day", "summary-filter-half-day"],
                  ["wfh", "summary-filter-wfh"],
                  ["leave", "summary-filter-leave"],
                  ["holiday", "summary-filter-holiday"],
                  ["weekly_off", "summary-filter-weekly-off"],
                ].map(([key, testid]) => (
                  <th key={key} className="px-2 py-2">
                    <input
                      type="number"
                      min={0}
                      value={sumFilters[key]}
                      onChange={(e) => setSumFilter(key, e.target.value)}
                      placeholder="Min"
                      title="Minimum days (inclusive)"
                      className="w-full border border-slate-200 rounded px-2 py-1 text-xs text-center focus:outline-none focus:ring-1 focus:ring-[#226DB4]/40"
                      data-testid={testid}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedSummary.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-slate-400 italic">
                    No employees match the column filters.
                  </td>
                </tr>
              ) : (
                sortedSummary.map((s) => (
                  <tr key={s.employee_id} className="border-t border-slate-100">
                    <td className="px-4 py-3 text-slate-700 font-medium">{s.employee_code}</td>
                    <td className="px-4 py-3 text-slate-800">{s.employee_name}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{s.status}</td>
                    <td className="px-3 py-3 text-center text-emerald-700 font-semibold">{s.present}</td>
                    <td className="px-3 py-3 text-center text-rose-600 font-semibold">{s.absent}</td>
                    <td className="px-3 py-3 text-center text-amber-600 font-semibold">{s.half_day}</td>
                    <td className="px-3 py-3 text-center text-blue-600 font-semibold">{s.wfh}</td>
                    <td className="px-3 py-3 text-center text-purple-600 font-semibold">{s.leave}</td>
                    <td className="px-3 py-3 text-center text-slate-500 font-semibold">{s.holiday}</td>
                    <td className="px-3 py-3 text-center text-indigo-600 font-semibold">{s.weekly_off || 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      </div>

      {/* Add modal */}
      {addOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form onSubmit={submitAttendance} className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-[#226DB4]" /> Add Attendance
            </h3>
            <select required value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className={inputCls} data-testid="att-employee-select">
              <option value="">Select employee</option>
              {rosterEmployees.map((e) => (
                <option key={e.id} value={e.id}>{e.basic.code} — {[e.basic.first_name, e.basic.middle_name, e.basic.last_name].filter(Boolean).join(" ")}</option>
              ))}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} data-testid="att-date-input" />
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls} data-testid="att-status-select">
                {Object.keys(STATUS_COLORS).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input type="time" placeholder="Check-in" value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} className={inputCls} />
              <input type="time" placeholder="Check-out" value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} className={inputCls} />
            </div>
            <textarea rows={2} placeholder="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inputCls} />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setAddOpen(false)} className="px-4 py-2 text-sm border border-slate-200 rounded">Cancel</button>
              <button type="submit" className="px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded" data-testid="att-save-btn">Save</button>
            </div>
          </form>
        </div>
      )}

      {/* Bulk modal */}
      {bulkOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Upload className="h-5 w-5 text-[#226DB4]" /> Bulk Attendance Upload
            </h3>
            <p className="text-xs text-slate-600">
              Paste rows in CSV format: <code className="bg-slate-100 px-1 rounded">code,date,status,check_in,check_out,note</code>
              <br />
              Status values: Present, Absent, Half-day, WFH, Leave, Holiday, Weekly Off
            </p>
            <textarea
              rows={10}
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              className="w-full border border-slate-200 rounded px-3 py-2 text-sm font-mono"
              data-testid="bulk-textarea"
            />
            {bulkResult && (
              <div className="text-sm bg-blue-50 border border-blue-200 rounded p-3" data-testid="bulk-result">
                Created: <b>{bulkResult.created}</b> · Skipped: <b>{bulkResult.skipped}</b>
                {bulkResult.errors?.length > 0 && (
                  <ul className="mt-2 text-xs text-rose-700 list-disc list-inside">
                    {bulkResult.errors.map((e, i) => <li key={`err-${i}-${e?.slice?.(0,20) || ''}`}>{e}</li>)}
                  </ul>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setBulkOpen(false); setBulkResult(null); }} className="px-4 py-2 text-sm border border-slate-200 rounded">Close</button>
              <button type="button" onClick={submitBulk} className="px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded" data-testid="bulk-submit-btn">Upload</button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form onSubmit={submitEdit} className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Edit Attendance</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {empMap[editing.employee_id]?.basic?.code} · {formatDisplayDate(editing.date)}
                </p>
              </div>
              <button type="button" onClick={() => setEditing(null)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Status</label>
              <select
                value={editForm.status}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                className={inputCls}
                data-testid="edit-att-status"
              >
                {["Present", "Absent", "Half-day", "WFH", "Leave", "Holiday", "Weekly Off"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Check-in</label>
                <input
                  type="time"
                  value={editForm.check_in}
                  onChange={(e) => setEditForm({ ...editForm, check_in: e.target.value })}
                  className={inputCls}
                  data-testid="edit-att-checkin"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Check-out</label>
                <input
                  type="time"
                  value={editForm.check_out}
                  onChange={(e) => setEditForm({ ...editForm, check_out: e.target.value })}
                  className={inputCls}
                  data-testid="edit-att-checkout"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Note</label>
              <input
                value={editForm.note}
                onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
                className={inputCls}
                placeholder="Optional remark"
                data-testid="edit-att-note"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 text-sm border border-slate-200 rounded">Cancel</button>
              <button type="submit" className="px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded" data-testid="edit-att-submit">
                Save Changes
              </button>
            </div>
          </form>
        </div>
      )}

      {deleting && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">Delete Attendance?</h3>
            <p className="text-sm text-slate-600">
              Delete attendance record for{" "}
              <span className="font-semibold">
                {[empMap[deleting.employee_id]?.basic?.first_name, empMap[deleting.employee_id]?.basic?.middle_name, empMap[deleting.employee_id]?.basic?.last_name].filter(Boolean).join(" ")}
              </span>{" "}
              on <span className="font-semibold">{formatDisplayDate(deleting.date)}</span>?
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
              ⚠ If a payslip for this month is already generated, regenerate it after deletion to update LOP.
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setDeleting(null)} className="px-4 py-2 text-sm border border-slate-200 rounded">Cancel</button>
              <button onClick={confirmDelete} className="px-4 py-2 text-sm bg-rose-600 hover:bg-rose-700 text-white rounded" data-testid="confirm-delete-att">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {quickOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 space-y-4" data-testid="quick-mark-modal">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Zap className="h-5 w-5 text-emerald-600" /> Quick Mark All Employees
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Pick a date (back-dated allowed) and apply the same status to every Active,
                  Onboarding, Probation, or Notice Period employee not yet marked that day.
                  Resigned, Inactive, Abscond, and Terminated are excluded. Existing records are kept.
                </p>
              </div>
              <button
                onClick={() => setQuickOpen(false)}
                className="p-1.5 text-slate-400 hover:bg-slate-100 rounded"
                data-testid="quick-mark-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                Date
              </label>
              <input
                type="date"
                data-testid="quick-mark-date"
                value={quickDate}
                onChange={(e) => { setQuickDate(e.target.value); setQuickResult(null); }}
                max={new Date().toISOString().slice(0, 10)}
                className={inputCls}
              />
            </div>

            <div className="grid grid-cols-2 gap-2 pt-1">
              <button
                disabled={quickBusy || !quickDate}
                onClick={() => runQuickMark("Present")}
                data-testid="quick-mark-present"
                className="inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm rounded font-medium bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
              >
                <ClipboardCheck className="h-4 w-4" /> Mark All Present
              </button>
              <button
                disabled={quickBusy || !quickDate}
                onClick={() => runQuickMark("Weekly Off")}
                data-testid="quick-mark-wo"
                className="inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm rounded font-medium bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-60"
              >
                <CalendarOff className="h-4 w-4" /> Mark All WO
              </button>
              <button
                disabled={quickBusy || !quickDate}
                onClick={() => runQuickMark("Holiday")}
                data-testid="quick-mark-holiday"
                className="inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm rounded font-medium bg-slate-600 hover:bg-slate-700 text-white disabled:opacity-60"
              >
                <CalendarHeart className="h-4 w-4" /> Mark Holiday
              </button>
              <button
                disabled={quickBusy || !quickDate}
                onClick={() => runQuickMark("WFH")}
                data-testid="quick-mark-wfh"
                className="inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm rounded font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60"
              >
                <Home className="h-4 w-4" /> Mark All WFH
              </button>
            </div>

            {quickBusy && (
              <div className="text-sm text-slate-500 text-center py-2">Applying… please wait</div>
            )}

            {quickResult && quickResult.ok && (
              <div
                data-testid="quick-mark-success"
                className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-800"
              >
                <div className="font-semibold mb-0.5">
                  Done — marked as <span className="uppercase">{quickResult.status}</span> for {formatDisplayDate(quickResult.date)}
                </div>
                <div className="text-xs">
                  Newly marked: <b>{quickResult.newly_marked}</b>
                  {" · "}Eligible for date: <b>{quickResult.total_eligible ?? quickResult.total_active_employees}</b>
                  {" · "}Skipped (already had records): <b>{quickResult.skipped_already_marked}</b>
                </div>
                {quickResult.skipped_not_eligible && (
                  <div className="text-[11px] text-emerald-900/80 mt-1">
                    Not eligible (not marked): before joining {quickResult.skipped_not_eligible.before_joining || 0}
                    {" · "}include off {quickResult.skipped_not_eligible.include_off || 0}
                    {" · "}no joining date {quickResult.skipped_not_eligible.no_joining_date || 0}
                    {" · "}after LWD {quickResult.skipped_not_eligible.after_lwd || 0}
                  </div>
                )}
              </div>
            )}

            {quickResult && !quickResult.ok && (
              <div
                data-testid="quick-mark-error"
                className="bg-rose-50 border border-rose-200 rounded p-3 text-sm text-rose-700"
              >
                {String(quickResult.error)}
              </div>
            )}

            <div className="text-[11px] text-slate-500 italic pt-1">
              Tip: To overwrite an existing record, use the row’s Edit (pencil) icon.
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
